import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Command, Plus, Loader2, Flame, CalendarCheck, Gauge, Users, Quote, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const fmtMoney = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "—");

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className={`h-5 w-5 ${accent || "text-white/50"}`} />
      <p className="mt-3 text-3xl font-semibold tracking-tight text-white">{value}</p>
      <p className="mt-1 text-sm text-white/45">{label}</p>
    </div>
  );
}

function BarBlock({ title, data, color = "#00FF66" }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <h3 className="mb-4 text-sm font-semibold text-white/80">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-white/35">No data yet.</p>
      ) : (
        <div className="space-y-2.5">
          {entries.sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <span className="w-40 shrink-0 truncate text-sm text-white/65">{k}</span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: color }} />
              </div>
              <span className="w-7 shrink-0 text-right text-sm font-medium text-white">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DiscoveryInsights() {
  useDocumentTitle("FleetOS — Discovery Insights", true);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ins, list] = await Promise.all([api.get("/market/insights"), api.get("/market/responses")]);
      setData(ins.data); setRows(list.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { document.documentElement.classList.add("dark"); load(); }, []);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-black"><Loader2 className="h-6 w-6 animate-spin text-white/50" /></div>;
  }

  const d = data || {};
  const priorityColor = (p) => p === "Hot" ? "text-[#00FF66]" : p === "Warm" ? "text-amber-400" : "text-white/50";

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3.5">
          <div className="flex items-center gap-2 font-semibold">
            <Command className="h-5 w-5" strokeWidth={1.75} />
            <span className="flex flex-col leading-tight sm:flex-row sm:items-baseline sm:gap-1.5">
              <span>FleetOS</span><span className="text-[11px] font-normal text-white/40 sm:text-sm">Discovery Insights</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="refresh-insights" onClick={load} className="rounded-full border border-white/15 p-2 text-white/60 hover:text-white"><RefreshCw className="h-4 w-4" /></button>
            <Link to="/discovery" data-testid="new-discovery" className="flex items-center gap-1.5 rounded-full bg-[#00FF66] px-4 py-2 text-sm font-medium text-black hover:bg-[#00FF66]/90"><Plus className="h-4 w-4" /> New call</Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-10">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Market validation board</h1>
        <p className="mt-2 text-white/50">Everything your agents are hearing on the ground, rolled up.</p>

        {/* KPIs */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi icon={Users} label="Calls logged" value={d.total ?? 0} />
          <Kpi icon={Flame} label="Hot leads" value={d.hot_count ?? 0} accent="text-[#00FF66]" />
          <Kpi icon={CalendarCheck} label="Demos requested" value={d.demo_requested ?? 0} accent="text-amber-400" />
          <Kpi icon={Gauge} label="Avg opportunity" value={`${d.avg_opportunity_score ?? 0}/10`} />
        </div>

        {/* pricing strip */}
        <div className="mt-4 grid grid-cols-2 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:grid-cols-5">
          {[["Avg weekday", d.pricing?.avg_weekday], ["Avg weekend", d.pricing?.avg_weekend], ["Avg peak", d.pricing?.avg_peak], ["Avg deposit", d.pricing?.avg_deposit], ["Avg SW budget", d.avg_software_budget]].map(([l, v]) => (
            <div key={l}><p className="text-xs text-white/45">{l}</p><p className="mt-1 text-lg font-semibold">{fmtMoney(v)}</p></div>
          ))}
        </div>

        {/* charts grid */}
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <BarBlock title="FleetOS need" data={d.needs?.fleetos_need} />
          <BarBlock title="Follow-up priority" data={d.by_priority} color="#f59e0b" />
          <BarBlock title="Fleet size" data={d.fleet_size} color="#3B9EFF" />
          <BarBlock title="Booking sources" data={d.booking_sources} color="#3B9EFF" />
          <BarBlock title="Top pain points" data={d.pain_points} color="#ef4444" />
          <BarBlock title="Vehicle types" data={d.vehicle_types} color="#a78bfa" />
          <BarBlock title="GPS need" data={d.needs?.gps_need} />
          <BarBlock title="Website need" data={d.needs?.website_need} />
          <BarBlock title="EV leasing need" data={d.needs?.ev_leasing_need} color="#00FF66" />
          <BarBlock title="EV expansion appetite" data={d.ev_expansion} color="#a78bfa" />
          <BarBlock title="EV barriers" data={d.ev_barriers} color="#ef4444" />
          <BarBlock title="Call status" data={d.by_status} color="#3B9EFF" />
        </div>

        {/* HOT LEADS */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-white/80"><Flame className="h-4 w-4 text-[#00FF66]" /> Hot leads (priority or score ≥ 7)</h3>
          {(d.hot_leads || []).length === 0 ? <p className="text-sm text-white/35">No hot leads yet.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="hot-leads-table">
                <thead><tr className="border-b border-white/10 text-left text-white/45">
                  <th className="pb-2 pr-4 font-medium">Company</th><th className="pb-2 pr-4 font-medium">Contact</th>
                  <th className="pb-2 pr-4 font-medium">Mobile</th><th className="pb-2 pr-4 font-medium">Location</th>
                  <th className="pb-2 pr-4 font-medium">Priority</th><th className="pb-2 pr-4 font-medium">Demo</th><th className="pb-2 font-medium">Score</th>
                </tr></thead>
                <tbody>
                  {d.hot_leads.map((l) => (
                    <tr key={l.id} className="border-b border-white/5">
                      <td className="py-2.5 pr-4 font-medium text-white">{l.company}</td>
                      <td className="py-2.5 pr-4 text-white/70">{l.contact_person || "—"}</td>
                      <td className="py-2.5 pr-4 text-white/70">{l.mobile || "—"}</td>
                      <td className="py-2.5 pr-4 text-white/70">{l.location || "—"}</td>
                      <td className={`py-2.5 pr-4 font-medium ${priorityColor(l.priority)}`}>{l.priority || "—"}</td>
                      <td className="py-2.5 pr-4 text-white/70">{l.demo || "—"}</td>
                      <td className="py-2.5 font-semibold text-white">{l.score ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* MAGIC WAND quotes */}
        <div className="mt-8">
          <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-white/80"><Quote className="h-4 w-4 text-[#00FF66]" /> "If I could magically solve one problem…"</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {(d.magic_wand || []).length === 0 ? <p className="text-sm text-white/35">No answers yet.</p> :
              d.magic_wand.map((m, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <p className="text-sm leading-relaxed text-white/85">“{m.answer}”</p>
                  <p className="mt-2 text-xs text-white/40">— {m.company}</p>
                </div>
              ))}
          </div>
        </div>

        {/* ALL CALLS */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="mb-4 text-sm font-semibold text-white/80">All calls ({rows.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="all-calls-table">
              <thead><tr className="border-b border-white/10 text-left text-white/45">
                <th className="pb-2 pr-4 font-medium">Date</th><th className="pb-2 pr-4 font-medium">Company</th>
                <th className="pb-2 pr-4 font-medium">Agent</th><th className="pb-2 pr-4 font-medium">Status</th>
                <th className="pb-2 pr-4 font-medium">FleetOS need</th><th className="pb-2 pr-4 font-medium">Priority</th><th className="pb-2 font-medium">Score</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5">
                    <td className="py-2.5 pr-4 text-white/60">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="py-2.5 pr-4 font-medium text-white">{r.company}</td>
                    <td className="py-2.5 pr-4 text-white/70">{r.agent_name || "—"}</td>
                    <td className="py-2.5 pr-4 text-white/70">{r.status || "—"}</td>
                    <td className="py-2.5 pr-4 text-white/70">{r.fleetos_need || "—"}</td>
                    <td className={`py-2.5 pr-4 font-medium ${priorityColor(r.priority)}`}>{r.priority || "—"}</td>
                    <td className="py-2.5 font-semibold text-white">{r.score ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
